import { useMemo } from "react";
import type {
  ActivitiesCancelScenario,
  ActivityReservation,
  Prices,
} from "@b2bportal/rbc-activities-api";
import { ActivitiesCancelScenarioEnum } from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";

export const useActivityCancelPolicyBreakdown = (
  cancelScenario: ActivitiesCancelScenario | undefined | null,
  activityReservation: ActivityReservation | undefined
) => {
  const { formatCurrency, t } = useI18nContext();

  const isFullyRefundable =
    cancelScenario?.ActivitiesCancelScenario ===
    ActivitiesCancelScenarioEnum.FullyRefundable;

  const isPartiallyRefundable =
    cancelScenario?.ActivitiesCancelScenario ===
    ActivitiesCancelScenarioEnum.PartiallyRefundable;

  const isRefundable = isFullyRefundable || isPartiallyRefundable;

  const breakdown = useMemo(() => {
    if (!isRefundable || !cancelScenario || !activityReservation) return null;

    const formatPrices = (prices: Prices | undefined): string | null => {
      if (!prices?.fiat) return null;
      return formatCurrency(prices.fiat.value, {
        currency: prices.fiat.currencyCode,
      });
    };

    const summary = cancelScenario.refundSummary;
    const items: Array<{ label: string; value: string }> = [];

    const totalPenalty = formatPrices(summary?.totalPenalty);
    const totalCompensation = formatPrices(summary?.totalCompensation);

    if (totalPenalty != null) {
      items.push({
        label: t("cancelFlightModal.refundSummary.totalPenalty"),
        value: totalPenalty,
      });
    }

    if (totalCompensation != null) {
      items.push({
        label: t("cancelFlightModal.refundSummary.totalCompensation"),
        value: totalCompensation,
      });
    }

    if (items.length === 0) return null;

    return { items };
  }, [isRefundable, cancelScenario, formatCurrency, t]);

  return { isRefundable, breakdown };
};
